/**
 * mfe-navigation.ts
 *
 * Navigation helpers for MFE mode initial route resolution.
 * Extracted from webgov-element.ts so they can be unit-tested without
 * bootstrapping the full Angular platform.
 */

import { StateService, TransitionService, UrlService, RawParams } from '@uirouter/core';
import { SessionService } from '@monsido/core/session/session.service';
import {
    buildShellPathIndex,
    matchShellPath,
    extractCustomerId,
} from '@client/app/services/shell-route-registry';
import {
    hasEventNavigation,
    getPendingShellNavigation,
    setPendingShellNavigation,
    setSuppressDashboardPublish,
    clearDashboardPublishSuppression,
} from '@client/app/services/navigation-coordinator';

/** @internal Reset the cached index — for tests only. */
export function _resetShellRouteIndex (): void {
    // No-op — index is rebuilt on every call now. Kept for test API compat.
}

/** Strip a leading '/webgov' and any leading slash; query string is preserved. */
export function normalizeShellPathForUrl (navigationPath: string): string {
    let p = navigationPath.trim();
    if (p.startsWith('/webgov')) {
        p = p.slice('/webgov'.length);
    }
    if (p.startsWith('/')) {
        p = p.slice(1);
    }
    return p;
}

/**
 * Resolve the shell-provided navigationPath to a UI-Router state and navigate
 * to it. Used on browser reload when the CC shell has set navigationPath.
 *
 * EAGER states navigate via a TargetState redirect returned from an onBefore
 * hook on base.customer.dashboard — DashboardComponent never renders.
 * LAZY states (e.g. accessibility issues-by-type/-page, fast-track) aren't
 * registered yet so we drive urlService after the dashboard lands.
 *
 * At bootstrap sessionService.customer is null — loaded async by the
 * customer-picker (base.dashboard). We go there first, wait for the customer,
 * then redirect before the dashboard renders.
 *
 * Falls back to base.dashboard when nothing resolves.
 */
export async function navigateToShellPath (
    navigationPath: string,
    stateService: StateService,
    sessionService: SessionService,
    transitionService: TransitionService,
    urlService: UrlService,
): Promise<void> {
    // If an event-based navigation (CTA/menu) already arrived this mount,
    // the deep-link is stale. Check whether the CTA already dispatched its own
    // navigation or is still queued (customer null).
    if (hasEventNavigation()) {
        const pending = getPendingShellNavigation();
        if (pending !== null) {
            return;
        }
        // CTA queued (customer null) — go to dashboard so customer picker loads
        // and the queue flushes.
        await stateService.go('base.dashboard', {});
        return;
    }

    const index = buildShellPathIndex(stateService.get());
    const match = matchShellPath(index, navigationPath);

    // Fast path: resolved to the customer dashboard → go there directly.
    if (match && match.stateName === 'base.customer.dashboard') {
        setPendingShellNavigation('base.customer.dashboard', {});
        await stateService.go('base.dashboard', {});
        return;
    }

    // Suppress the outgoing publish for the intermediate base.customer.dashboard
    // transition(s) so the CC shell doesn't flash the /domains URL.
    if (match) {
        setSuppressDashboardPublish(true);
    }

    // Intercept the base.customer.dashboard transition BEFORE it renders and
    // redirect straight to the target — DashboardComponent never mounts.
    // By the time onBefore fires the customer-picker has already populated
    // sessionService.customer, so customerId is available.
    const deregister = transitionService.onBefore(
        { to: 'base.customer.dashboard' },
        (transition) => {
            // Re-check — a CTA may have arrived while we were waiting for the
            // customer to load. If so, let the dashboard through (CTA queue
            // will flush and navigate).
            if (hasEventNavigation()) {
                deregister();
                clearDashboardPublishSuppression();
                return true;
            }

            deregister();

            const { customerId: urlCustomerId } = extractCustomerId(
                (() => {
                    let p = navigationPath.split('?')[0];
                    if (p.startsWith('/webgov')) {
                        p = p.slice('/webgov'.length) || '/';
                    }
                    if (!p.startsWith('/')) {
                        p = '/' + p;
                    }
                    return p;
                })(),
            );

            const sessionCustomerId = sessionService.customer?.id;
            const customerId =
                urlCustomerId ??
                (sessionCustomerId != null ? String(sessionCustomerId) : undefined) ??
                (transition.params().customerId != null ? String(transition.params().customerId) : undefined);

            if (customerId == null) {
                clearDashboardPublishSuppression();
                return true;
            }

            if (match) {
                // EAGER: redirect by returning a TargetState — DashboardComponent never renders.
                const goParams: RawParams = { ...match.params, customerId };
                setPendingShellNavigation(match.stateName, { ...goParams });
                const deregisterSuccess = transitionService.onSuccess(
                    { to: match.stateName },
                    () => {
                        deregisterSuccess();
                        clearDashboardPublishSuppression();
                    },
                );
                return stateService.target(match.stateName, goParams, { location: false });
            }

            // LAZY: abort the dashboard transition and drive the internal URL directly.
            // No pending suppression — let onSuccess publish navigation.changed
            // so CC learns the final resolved destination after lazy loading.
            const shellSegment = normalizeShellPathForUrl(navigationPath);
            const { path: pathWithoutCustomer, customerId: extractedId } = extractCustomerId(
                shellSegment.startsWith('/') ? shellSegment : `/${shellSegment}`,
            );
            const effectiveCustomerId = extractedId ?? customerId;
            const cleanedSegment = pathWithoutCustomer.replace(/^\//, '');
            const internalUrl = `/${effectiveCustomerId}/${cleanedSegment}`;

            setTimeout(() => {
                try {
                    urlService.url(internalUrl);
                    urlService.sync();
                } catch {
                    stateService.go('base.dashboard', {});
                }
                setTimeout(() => {
                    clearDashboardPublishSuppression();
                }, 0);
            }, 0);

            return false;
        },
    );

    await stateService.go('base.dashboard', {});
}
